import React from "react";
import { useMultiSidebar } from "@/components/ui/MultiSidebar";
import { SidebarHeader } from "@/components/ui/Sidebar";
import { Close } from "@/assets/svg/Close";
import { IconInput } from "@/components/FormInputs/IconInput";
import { Button } from "@/components/ui/Button";
import { Funnel, Search, ListCollapse } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { PartnerFeatureEnum } from "@/__generated__/types";
import { RiseAltLogo } from "@/assets/svg/RiseAltLogo";

interface FilterHeaderProps {
  onCollapseFilterGroups?: () => void;
  onSearchInputUpdate: (input: string) => void;
  collapseButtonDisabled?: boolean;
}

export const Header = ({
  onCollapseFilterGroups,
  onSearchInputUpdate,
  collapseButtonDisabled,
}: FilterHeaderProps) => {
  const { t } = useTranslation();
  const { setOpen } = useMultiSidebar("filter");
  const { hasOrgFeature } = useOrgPermissions();

  return (
    <SidebarHeader className="gap-4 p-4">
      <div className="flex h-7 w-full flex-row justify-between self-end">
        <div className="flex flex-row items-end">
          <div className="[&>svg]:size-4.5">
            <Funnel />
          </div>
          <h2 className="ml-2 text-lg leading-5 font-semibold text-foreground-medium">
            {t("filters.sidebarTitle")}
          </h2>
          {hasOrgFeature(PartnerFeatureEnum.RisePredictiveModels) && (
            <RiseAltLogo className="-mb-[2px] ml-2.5 h-6" />
          )}
        </div>
        <button
          aria-label={t("filters.closeSidebar")}
          data-testid="filters-close-btn"
          className="bg-input-icon/50 flex size-5 cursor-pointer items-center justify-center rounded-full opacity-40"
          onClick={() => setOpen(false)}
        >
          <Close className="text-foreground" />
        </button>
      </div>
      <div className="flex w-full flex-row items-center gap-2 self-start">
        <IconInput
          className="w-full"
          inputClassName="md:text-xs placeholder:text-primary/40"
          onChange={(e) => onSearchInputUpdate(e.target.value)}
          icon={Search}
          data-testid="filter-search-input"
          placeholder={t("filters.searchPlaceholder")}
        />
        <Button
          data-testid="collapse-btn"
          variant="outline"
          disabled={collapseButtonDisabled}
          className="w-9"
          aria-label={t("filters.collapseFilterGroups")}
          onClick={onCollapseFilterGroups}
        >
          <ListCollapse />
        </Button>
      </div>
    </SidebarHeader>
  );
};
